<template>
  <LISAButton
    v-bind="$attrs"
    type="button"
    rounded
    icon="fas fa-users"
    :severity="!activeMeeting ? 'secondary' : undefined"
    :class="{
      'active-meeting': activeMeeting,
    }"
    :variant="!activeMeeting ? 'secondary' : undefined"
    @click="toggle"
  ></LISAButton>
  <PrimeMenu
    ref="menu"
    :model="items"
    class="HeadNavBarScoped"
    :popup="true"
    style="font-size: 0.8em; width: 25em"
  >
  </PrimeMenu>
</template>

<script lang="ts">
import { useTranslation } from "@/language/translation";
import { useStore } from "vuex";
import { computed, defineComponent, ref } from "vue";
import LISAButton from "@LISAPrime/LISAButton.vue";
import PrimeMenu from "primevue/menu";
import type { MenuItem } from "primevue/menuitem";
import { ENV } from "@/ENV";

import {
  useMeetingsQuery,
  MeetingsChangedDocument,
  usefinishMeetingMutation,
  usecreateMeetingMutation,
  useJwtForRoomMutation,
} from "@/graphql";

export default defineComponent({
  name: "MeetItem",
  components: {
    LISAButton,
    PrimeMenu,
  },
  inheritAttrs: false,
  props: {
    boardId: {
      type: Number,
      required: true,
    },
  },
  setup(props) {
    const menu = ref<InstanceType<typeof PrimeMenu>>();
    const { result, subscribeToMore } = useMeetingsQuery();
    subscribeToMore({
      document: MeetingsChangedDocument,
      updateQuery: (_previousResult, { subscriptionData }) => {
        return {
          meetings: subscriptionData.data.meetings,
        };
      },
    });
    const { t } = useTranslation();
    const toggle = (event: any) => {
      menu.value?.toggle(event);
    };
    const meetings = computed(() => result.value?.meetings ?? []);

    const store = useStore();

    const userId = store.getters["login/userId"];

    const otherMeetings = computed(() => {
      return meetings.value.filter((meeting) => {
        return meeting.board.id !== props.boardId;
      });
    });
    const meetingsForThisBoard = computed(() => {
      return meetings.value.filter((meeting) => {
        return meeting.board.id === props.boardId && meeting.creator?.id !== userId;
      });
    });
    const myMeeting = computed(() => {
      return meetings.value.find((meeting) => {
        return meeting.board.id === props.boardId && meeting.creator?.id === userId;
      });
    });

    const activeMeeting = computed(() => meetingsForThisBoard.value.length > 0 || myMeeting.value);

    const { mutate: finishMeetingMutation } = usefinishMeetingMutation({});
    const { mutate: createMeetingMutation } = usecreateMeetingMutation(() => ({
      variables: { boardId: props.boardId },
    }));

    const { mutate: getJWT } = useJwtForRoomMutation({});

    const joinMeeting = (roomName?: string) => {
      // get a reference to window.open synchronously, to support new tab functionality in iOS
      if (roomName == null) {
        return;
      }
      const windowReference = window.open();
      const meetingUrl = ENV.VITE_APP_JITSI_URL || "https://meet.lisapp.dev";

      // not using async here intentionally, to support setting windowReference.location asynchronously
      getJWT({ roomName }).then((data) => {
        if (data?.data?.jwtForRoom && windowReference) {
          const link = `${meetingUrl}/${roomName}?jwt=${data.data.jwtForRoom}`;
          windowReference.location = link;
        }
      });
    };
    function finishMeeting(roomName: string | undefined) {
      if (roomName == null) {
        return;
      }
      finishMeetingMutation({ roomName });
    }
    async function createMeeting() {
      const data = await createMeetingMutation();
      joinMeeting(data?.data?.createMeeting.roomName);
    }

    const items = computed<MenuItem[]>(() => {
      const menuItems: MenuItem[] = [];
      /**
       * pushing my meeting when one is available
       *
       */
      if (myMeeting.value != null) {
        menuItems.push({
          label: t("components.MeetItem.myMeetingsHeader"),
          items: [
            {
              label: t("components.MeetItem.joinMyMeeting"),
              icon: "fas fa-sign-in",
              command: () => joinMeeting(myMeeting.value?.roomName),
            },
            {
              label: t("components.MeetItem.finishMyMeeting"),
              icon: "fas fa-times-circle text-error",
              command: () => finishMeeting(myMeeting.value?.roomName),
            },
          ],
        });
      }

      /**
       * pushing create and join operation if board is available
       */
      if (props.boardId && myMeeting.value == null) {
        menuItems.push({
          label: t("components.MeetItem.meetings"),
          items: [
            {
              label: t("components.MeetItem.createMeeting"),
              icon: "fas fa-plus",
              command: () => createMeeting(),
            },
          ],
        });
      }
      if (meetingsForThisBoard.value.length > 0) {
        menuItems.push({
          label: t("components.MeetItem.thisBoardMeetingsHeader"),
          items: meetingsForThisBoard.value.map((boardMeetings) => {
            return {
              icon: "fas fa-sign-in",
              label: t("components.MeetItem.joinMeetingBy", {
                by: boardMeetings.creator?.preferredName,
              }),
              command: () => joinMeeting(boardMeetings.roomName),
            };
          }),
        });
      }
      /**
       * pushing Meetings for other boards
       */
      menuItems.push({
        label: t("components.MeetItem.otherBoardMeetingsHeader"),
        items: otherMeetings.value.map((otherMeetingsForMenu) => {
          return {
            icon: "fas fa-sign-in",
            label: otherMeetingsForMenu.board.name,
            command: () => joinMeeting(otherMeetingsForMenu.roomName),
          };
        }),
      });
      menuItems.push({
        icon: "fas fa-spinner fa-spin",
        label: t("components.MeetItem.liveLoading"),
      });
      return menuItems;
    });

    return {
      myMeeting,
      menu,
      items,
      activeMeeting,
      otherMeetings,
      meetingsForThisBoard,
      joinMeeting,
      toggle,
      createMeeting,
      finishMeeting,
      finishMeetingMutation,
      createMeetingMutation,
    };
  },
});
</script>

<style lang="scss">
.active-meeting {
  background-color: var(--p-warn-color) !important;
  border-color: var(--p-warn-color) !important;

  &:hover,
  &:active {
    background-color: var(--p-warn-color) !important;
  }
}
</style>
